import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCartStore, useWishlistStore, useUIStore } from '../../store';
import { formatPrice, getDiscountPercent } from '../../utils/helpers';
import StarRating from '../common/StarRating';
import { trackAddToCart } from '../../utils/pixel';
import toast from 'react-hot-toast';

const ProductCard = ({ product, index = 0 }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const { addItem } = useCartStore();
  const { toggle, isWishlisted } = useWishlistStore();
  const { setCartOpen } = useUIStore();

  const wished = isWishlisted(product.id);

  // For products with variations, find the cheapest variation value (after its own
  // sale price if it has one) so the card price matches what the customer will
  // actually see on the product page — not just the base/default price.
  const firstVariationValues = product.variations?.[0]?.values || [];
  const pricedOptions = firstVariationValues.length > 0
    ? firstVariationValues.map(v => ({
        regular: v.price ?? product.price,
        sale: v.salePrice ?? null,
      }))
    : [{ regular: product.price, sale: product.salePrice ?? null }];

  const effectiveOptions = pricedOptions.map(o => ({
    regular: o.regular,
    final: (o.sale != null && o.sale < o.regular) ? o.sale : o.regular,
    onSale: o.sale != null && o.sale < o.regular,
  }));

  const cheapest = effectiveOptions.reduce((min, o) => (o.final < min.final ? o : min), effectiveOptions[0]);
  const hasSale = cheapest.onSale;
  const displayPrice = cheapest.final;
  const discount = hasSale ? getDiscountPercent(cheapest.regular, cheapest.final) : 0;
  const hasMultiplePrices = new Set(effectiveOptions.map(o => o.final)).size > 1;
  const images = product.images || [];

  const handleAddToCart = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (product.stock <= 0) { toast.error('Out of stock'); return; }
    addItem(product, 1);
    trackAddToCart(product, 1);
    setCartOpen(true);
    toast.success('Added to cart!');
  };

  const handleWishlist = (e) => {
    e.preventDefault(); e.stopPropagation();
    toggle(product);
    toast.success(wished ? 'Removed from wishlist' : 'Added to wishlist!');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="product-card group bg-white flex flex-col"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setImgIdx(0); }}
    >
      <Link to={`/product/${product.id}`} className="block">
        {/* Image */}
        <div className="relative overflow-hidden aspect-square bg-gray-50">
          <motion.img
            src={images[hovered && images.length > 1 ? 1 : imgIdx] || 'https://via.placeholder.com/400x400?text=SitSmart'}
            alt={product.title}
            className="w-full h-full object-contain p-4 transition-all duration-700"
            animate={{ scale: hovered ? 1.06 : 1 }}
            transition={{ duration: 0.6 }}
            onError={e => e.target.src = 'https://via.placeholder.com/400x400?text=Chair'}
          />
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.isNew && <span className="badge-new">New</span>}
            {hasSale && <span className="badge-sale">-{discount}%</span>}
            {product.stock <= 0 && <span className="bg-gray-700 text-white text-[10px] font-black px-2 py-0.5 uppercase tracking-widest">Sold Out</span>}
          </div>
          {/* Wishlist button - top right */}
          <button
            onClick={handleWishlist}
            className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center shadow-md transition-all duration-300 ${wished ? 'bg-[#e63946] text-white' : 'bg-white text-gray-500 hover:text-[#e63946]'}`}
          >
            <svg className="w-3.5 h-3.5" fill={wished ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
            </svg>
          </button>
          {/* Image dots */}
          {images.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.slice(0, 4).map((_, i) => (
                <button key={i} onMouseEnter={() => setImgIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${imgIdx === i ? 'bg-white w-3' : 'bg-white/50 w-1.5'}`}/>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-2 group-hover:text-[#1a3a6e] transition-colors truncate flex-1" title={product.title}>
            {product.title}
          </h3>
          {product.rating > 0 && (
            <div className="flex items-center gap-1.5 mb-2">
              <StarRating rating={product.rating} size="sm"/>
              <span className="text-xs text-gray-400">({product.reviewCount || 0})</span>
            </div>
          )}
          <div className="flex items-baseline gap-2 mb-3">
            {hasMultiplePrices && <span className="text-xs text-gray-400 font-medium">From</span>}
            <span className="font-black text-[#1a3a6e] text-base">{formatPrice(displayPrice)}</span>
            {hasSale && <span className="text-xs text-gray-400 line-through">{formatPrice(cheapest.regular)}</span>}
          </div>
          {/* Permanent Add to Cart button */}
          <button
            onClick={handleAddToCart}
            disabled={product.stock <= 0}
            className={`w-full flex items-center justify-center gap-2 py-2.5 text-xs font-black tracking-wide transition-all duration-300 ${
              product.stock <= 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#1a3a6e] text-white hover:bg-[#e63946]'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
            </svg>
            {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
          </button>
        </div>
      </Link>
    </motion.div>
  );
};
export default ProductCard;
